import { computed, ref } from 'vue';
import { useDisplay } from 'vuetify';

export type SideBarEntityType = 'user' | 'group' | 'user-multiple' | 'group-multiple' | undefined;

// shared state for the side bar
const sideBarOpen = ref(false);
const selectedEntity = ref<SideBarEntityType | null>(null);
const selectedEntityUid = ref<string | null>(null);
const sideBarSubTitle = ref<string | null>(null);

// Action queue to ensure sequential execution
const actionQueue: (() => Promise<void>)[] = [];
let isProcessing = false;

async function processQueue() {
  if (isProcessing || actionQueue.length === 0) return;

  isProcessing = true;
  while (actionQueue.length > 0) {
    const action = actionQueue.shift();
    if (action) {
      await action();
    }
  }
  isProcessing = false;
}

export function useSideBar() {
  const { smAndDown, width } = useDisplay();

  const enqueueAction = (action: () => Promise<void>) => {
    actionQueue.push(action);
    processQueue();
  };

  const openSideBar = (entity: SideBarEntityType, uid?: string) => {
    enqueueAction(async () => {
      selectedEntity.value = entity;
      selectedEntityUid.value = uid ?? null;
      await new Promise((resolve) => setTimeout(resolve, 100)); // let transition finish
      sideBarOpen.value = true;
    });
  };

  const closeSideBar = () => {
    enqueueAction(async () => {
      await new Promise((resolve) => setTimeout(resolve, 50)); // let transition finish
      sideBarOpen.value = false;
      selectedEntity.value = null;
      selectedEntityUid.value = null;
      sideBarSubTitle.value = null;
    });
  };

  const sideBarWidth = computed(() => {
    const calculatedWidth = smAndDown.value ? width.value : width.value / 3;
    // if the width is less than 600px, it starts to look bad
    if (smAndDown.value) {
      return calculatedWidth < 600 ? 680 : calculatedWidth;
    }
    return calculatedWidth;
  });

  return {
    sideBarOpen,
    selectedEntity,
    selectedEntityUid,
    sideBarWidth,
    openSideBar,
    closeSideBar,
  };
}
